import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import PageBreadcrumb from "../components/common/PageBreadCrumb";
import PageMeta from "../components/common/PageMeta";
import { getStoredToken } from '../utils/authStorage';
import Badge from '../components/ui/badge/Badge';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000';

interface OrderItem {
    id: number;
    created_at: string;
    total: string;
    amount?: string;
    status?: string;
    statuses?: { name: string }[];
    payment_mode?: { name: string };
    products?: { id: number; name: string; pivot: { product_quantity: number }; price: string }[];
    invoices?: { id: number; url: string; code: string }[];
}

const STATUS_TABS = [
    { key: 'all', label: 'All Orders' },
    { key: 'Pending', label: 'Pending' },
    { key: 'Processing', label: 'Processing' },
    { key: 'Delivered', label: 'Delivered' },
    { key: 'Cancelled', label: 'Cancelled' },
];

export default function CheckoutTestPage() {
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState<number>(100);

    // Orders state
    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('all');
    const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);

    const fetchMyOrders = async () => {
        setOrdersLoading(true);
        try {
            const token = getStoredToken();
            const res = await axios.get(`${API_BASE}/api/orders/orders`, {
                params: {
                    per_page: 100,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });

            let data: OrderItem[] = [];
            const payload = res.data;
            if (Array.isArray(payload)) {
                data = payload;
            } else if (Array.isArray(payload?.data)) {
                data = payload.data;
            } else if (Array.isArray(payload?.data?.data)) {
                data = payload.data.data;
            }

            setOrders(data);
        } catch (err: any) {
            console.error('Failed to fetch user orders:', err);
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        fetchMyOrders();
    }, []);

    const handlePayNow = async () => {
        setLoading(true);
        try {
            const token = getStoredToken();
            const res = await fetch(`${API_BASE}/api/payment/initiate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ amount }),
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.message || 'Payment initiation failed');
            }

            const json = await res.json();
            const data = json.data;

            if (!data) {
                throw new Error('Invalid response from server');
            }

            if (data.redirectUrl) {
                window.location.href = data.redirectUrl;
            } else if (data.formUrl && data.formParams) {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = data.formUrl;

                Object.keys(data.formParams).forEach(key => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = data.formParams[key];
                    form.appendChild(input);
                });

                document.body.appendChild(form);
                form.submit();
            } else if (data.clientSecret) {
                Swal.fire({
                    title: 'Stripe Payment',
                    text: `Stripe clientSecret received: ${data.clientSecret}. In a real flow, this would open Stripe Elements.`,
                    icon: 'info'
                });
            } else {
                throw new Error('Unknown payment response format');
            }

        } catch (err: any) {
            console.error('Failed to initiate payment:', err);
            Swal.fire({
                title: 'Error',
                text: err.message || 'Failed to initiate payment. Make sure a gateway is active.',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageMeta
                title="Checkout & Orders | Shoplattice Customer Portal"
                description="Test payment flow and check your order history across different states."
            />
            <PageBreadcrumb pageTitle="Checkout & My Orders" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6 max-w-7xl mx-auto px-4">
                {/* Left Side: Checkout Payment Gateway Test */}
                <div className="lg:col-span-1">
                    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                            <h3 className="text-md font-bold text-gray-800 dark:text-white">🚀 Initiate Checkout Payment</h3>
                            <p className="text-xs text-gray-500 mt-1">Test active payment provider configuration.</p>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">Test Amount ($)</label>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(Number(e.target.value))}
                                    className="w-full px-3 py-2 text-sm border rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-brand-500"
                                />
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/10 flex justify-between items-center">
                            <div className="text-md font-bold text-gray-900 dark:text-white">Total: ${amount}</div>
                            <button
                                onClick={handlePayNow}
                                disabled={loading}
                                className="px-5 py-2 text-sm font-semibold text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
                            >
                                {loading ? 'Processing...' : 'Pay Now'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Side: My Orders List & Filtering */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                            <div>
                                <h3 className="text-md font-bold text-gray-800 dark:text-white">📦 My Order History</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Your front-facing orders mapped by states.</p>
                            </div>
                            <button 
                                onClick={() => fetchMyOrders()}
                                className="text-xs text-brand-500 hover:underline"
                            >
                                Refresh
                            </button>
                        </div>

                        {/* Order States Tabs */}
                        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/20 dark:bg-gray-800/10 flex gap-2 overflow-x-auto">
                            {STATUS_TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${
                                        activeTab === tab.key
                                            ? 'bg-brand-500 text-white'
                                            : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Orders List Container */}
                        <div className="p-4 space-y-4 divide-y divide-gray-100 dark:divide-gray-800">
                            {ordersLoading ? (
                                <div className="py-10 text-center text-sm text-gray-500">
                                    Loading your order history...
                                </div>
                            ) : (() => {
                                const filteredOrders = orders.filter(order => {
                                    const rawStatus = order.status || order.statuses?.[0]?.name || 'Pending';
                                    if (activeTab === 'all') return true;
                                    return rawStatus.toLowerCase() === activeTab.toLowerCase();
                                });

                                if (filteredOrders.length === 0) {
                                    return (
                                        <div className="py-10 text-center text-sm text-gray-500">
                                            No orders found matching this state.
                                        </div>
                                    );
                                }

                                return filteredOrders.map(order => {
                                    const rawStatus = order.status || order.statuses?.[0]?.name || 'Pending';
                                    return (
                                        <div key={order.id} className="pt-4 first:pt-0">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <span className="text-sm font-bold text-gray-800 dark:text-white">Order #{order.id}</span>
                                                    <span className="text-xs text-gray-400 block mt-0.5">
                                                        Placed on {new Date(order.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <Badge
                                                    size="sm"
                                                    color={
                                                        rawStatus === 'Delivered' || rawStatus === 'Complete'
                                                            ? 'success'
                                                            : rawStatus === 'Pending'
                                                            ? 'warning'
                                                            : rawStatus === 'Cancelled'
                                                            ? 'error'
                                                            : 'primary'
                                                    }
                                                >
                                                    {rawStatus}
                                                </Badge>
                                            </div>

                                            {/* Products Summary */}
                                            {order.products && order.products.length > 0 && (
                                                <div className="mt-3 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg space-y-1">
                                                    {order.products.map(p => (
                                                        <div key={p.id} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                                                            <span>{p.name} <strong className="text-gray-400">x{p.pivot?.product_quantity || 1}</strong></span>
                                                            <span>${p.price}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="mt-3 flex justify-between items-center">
                                                <div className="text-sm">
                                                    <span className="text-gray-400">Total:</span>{' '}
                                                    <strong className="text-gray-800 dark:text-white">${order.total || order.amount || '0.00'}</strong>
                                                </div>
                                                
                                                <div className="flex gap-2">
                                                    {order.invoices && order.invoices.length > 0 && (
                                                        <a
                                                            href={order.invoices[0].url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                                                        >
                                                            📄 Download Invoice
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => setSelectedOrder(order)}
                                                        className="px-3 py-1.5 text-xs font-semibold border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg transition-colors"
                                                    >
                                                        Details
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Details Drawer / Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div 
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                            aria-hidden="true"
                            onClick={() => setSelectedOrder(null)}
                        ></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="flex justify-between items-start border-b border-gray-100 dark:border-gray-800 pb-3">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Order Details #{selectedOrder.id}</h3>
                                    <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-500">
                                        &times;
                                    </button>
                                </div>
                                <div className="mt-4 space-y-3 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex justify-between">
                                        <span>Status:</span>
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            {selectedOrder.status || selectedOrder.statuses?.[0]?.name || 'Pending'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Payment Mode:</span>
                                        <span>{selectedOrder.payment_mode?.name || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Placed At:</span>
                                        <span>{new Date(selectedOrder.created_at).toLocaleString()}</span>
                                    </div>
                                    
                                    <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                                        <h4 className="font-bold text-gray-800 dark:text-white mb-2">Items</h4>
                                        <div className="space-y-2">
                                            {selectedOrder.products?.map(p => (
                                                <div key={p.id} className="flex justify-between text-xs">
                                                    <span>{p.name} x{p.pivot?.product_quantity || 1}</span>
                                                    <span>${p.price}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-700 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setSelectedOrder(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
